import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SignatureRequest } from '../models/signature-request.model';
import { Company } from '../models/company.model';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from './api.service';
import { UtilsService } from './utils.service';
import { Document } from '../models/document.model';
import { Meta } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { FaviconService } from './favicon.service';

@Injectable({
  providedIn: 'root'
})
export class SignatureRequestService {

  // company
  private company_subject = new BehaviorSubject<Company>(null as any);
  company = this.company_subject.asObservable();

  // signature request
  private signature_request_subject = new BehaviorSubject<SignatureRequest>(null as any);
  signature_request = this.signature_request_subject.asObservable();
  private fetching_signature_request_uuid:string = null as any;

  // signature request language
  private signature_request_language_subject = new BehaviorSubject<string>(null as any);
  signature_request_language = this.signature_request_language_subject.asObservable();

  // signature request description
  private signature_request_description_subject = new BehaviorSubject<string>(null as any);
  signature_request_description = this.signature_request_description_subject.asObservable();
  current_signature_request_description:string = this.signature_request_description_subject.value;

  // document
  private document_subject = new BehaviorSubject<Document>(null as any);
  document = this.document_subject.asObservable();

  constructor(private activated_route:ActivatedRoute,
              private api:ApiService,
              private utils:UtilsService,
              private meta:Meta,
              private translate:TranslateService,
              private favicon:FaviconService) {

              }

  init(signature_request_uuid:string|null) {
    if(signature_request_uuid!=null && (this.signature_request_subject.value==null || this.signature_request_subject.value.uuid!=signature_request_uuid)) {
      // fetch signature request
      this.fetchSignatureRequest(signature_request_uuid);
    }
  }

  obtainSignatureRequestLanguage(signature_request_uuid:string|null) {
    if(signature_request_uuid!=null) {
      this.fetchSignatureRequestLanguage(signature_request_uuid);
    }
  }


  getCompany() {
    return this.company_subject.value;
  }

  setCompany(company:Company) {
    this.company_subject.next(company);
    this.initColorPalette();
    this.favicon.setFromCompany(company);
  }

  getSignatureRequest(): SignatureRequest {
    return this.signature_request_subject.value;
  }

  setSignatureRequest(signature_request:SignatureRequest) {
    this.signature_request_subject.next(signature_request);
    if(signature_request.requester.company!=null) this.setCompany(signature_request.requester.company);
  }

  getDocument() {
    return this.document_subject.value;
  }

  setDocument(d:Document) {
    this.document_subject.next(d);
  }

  resetSignatureRequest() {
    this.signature_request_subject.next(null as any);
    this.company_subject.next(null as any);
  }

  reset() {
    this.resetSignatureRequest();
  }

  private fetchSignatureRequest(signature_uuid:string) {
    if(this.fetching_signature_request_uuid==null || this.fetching_signature_request_uuid!=signature_uuid) {
      this.fetching_signature_request_uuid = signature_uuid;
      this.api.getSignatureRequest(signature_uuid, { with: "requester.company,signatories.user,documents" }).subscribe(
        data => {
          this.setSignatureRequest(data.data);
        }
      );
    }
  }

  private fetchSignatureRequestLanguage(signature_uuid:string) {
    this.api.getSignatureRequestBasicInfo(signature_uuid).subscribe(
      (data:any) => {
        // get language from JsonResponseSingle
        if (data && data.language) this.translate.use(data.language);
        this.signature_request_language_subject.next(data.language);
        this.signature_request_description_subject.next(data.description);
      }
    );
  }

  private initColorPalette() {
    const company = this.company_subject.value;
    if(company!=null && company.color!=null) {
      // variables de CSS
      document.body.classList.remove("light");
      document.body.classList.remove("dark");
      document.body.classList.add('text_' + company.text_color);
      document.body.style.setProperty(`--accent-color`, company.color);
      document.body.style.setProperty(`--primary-color`, company.color);
      document.body.style.setProperty(`--primary-darker-color`, this.utils.darkerColor(company.color));
      document.body.style.setProperty(`--primary-lighter-color`, this.utils.lighterColor(company.color));
      document.body.style.setProperty(`--accent-darker-color`, this.utils.darkerColor(company.color));
      document.body.style.setProperty(`--accent-lighter-color`, this.utils.lighterColor(company.color));

      // Meta tag theme-color
      this.setMetatagThemeColor(company.color);
    }
  }

  private setMetatagThemeColor(color:string) {
    this.meta.updateTag({ content: color }, 'name=theme-color');
  }
}
